import * as React from 'react';
import * as SVGInjector from 'svg-injector';
import * as R from 'ramda';

interface IconProps {
  path: string;
  className?: string;
  id?: string;
  fallbackPath?: string;
  style?: React.CSSProperties;
  verticalAlign?: string;
}
class Icon extends React.Component<IconProps> {
  isInjected = false;

  /**
   * Prevent re-injecting the same image path
   */
  shouldComponentUpdate(nextProps: IconProps) {
    return !this.isInjected || !R.whereEq(nextProps, this.props);
  }

  inject(element: Parameters<typeof SVGInjector>[0]) {
    this.isInjected = true;
    SVGInjector(element, {
      evalScripts: 'never',
      each: svg => {
        const isSVGElement = (
          s: string | undefined | SVGElement
        ): s is SVGElement =>
          svg !== undefined && (svg as SVGElement).setAttribute !== undefined;

        if (isSVGElement(svg)) {
          svg.style.verticalAlign = this.props.verticalAlign || 'middle';
        }
      },
    });
  }

  ref(span?: HTMLSpanElement) {
    if (!span || span.children.length === 0) {
      return;
    }

    if (this.isInjected) {
      const newImg = new Image();
      newImg.setAttribute('src', this.props.path);
      newImg.setAttribute('data-fallback', this.props.fallbackPath || '');
      newImg.setAttribute('id', this.props.id || '');
      newImg.setAttribute('class', this.props.className || '');
      swapElements(span, newImg);
      span.replaceChild(newImg, span.children[0]);
    }

    this.inject(span.children[0]);
  }

  render() {
    const { id, className, path, fallbackPath } = this.props;

    return (
      <span
        className={`IconWrapper ${className ? `${className}__wrapper` : ''}`}
        ref={span => this.ref(span as HTMLSpanElement)}
        style={this.props.style}
      >
        <img
          alt=""
          className={className}
          id={id || ''}
          src={path}
          data-fallback={fallbackPath}
        />
      </span>
    );
  }
}

/**
 * Removes first child from `parent`, insert it into a new wrapper element,
 * and then inserts `newChild` into `parent`. Like `parent.replaceChild(new, old)`
 * but `old` will have a parent node (but not attached to DOM).
 * This is so that SVGInjector does not fail due to the old child not having a
 * `.parentNode`.
 */
const swapElements = (parent: HTMLElement, newChild: HTMLElement) => {
  const parentPlaceholder = window.document.createElement('span');
  parentPlaceholder.appendChild(parent.children[0]);
  parent.appendChild(newChild);
};

export default Icon;
